import { useMemo, useState } from 'react';
import { useI18n } from '../../context/useI18n';
import { useAuth } from '../../context/useAuth';
import {
  getServicesFor,
  getD28dProgramId,
  isCoachClientUser,
  isVirtualGymMember,
  hideFoodForGymVirtualConsumer,
  isFoodOnlyCommercialUser,
} from './userServices';
import { isCoachWhiteLabelActive, mergeCoachBrand } from '../../utils/branding';
import { useFrontendConfig } from '../../context/FrontendConfigContext';
import { openFoodModule, redirectToContratarFood } from '../../utils/foodModule';
import { futbolDestForUser, openFutbolModule, resolveFutbolBrandName } from '../../utils/futbolModule';
import { isTrainingExternal, openTrainingModule } from '../../utils/trainingModule';
import { isSedeFinalUser } from '../../utils/gymRoleScope';
import { isConsumerAccessBlocked } from '../../utils/subscriptionAccess';
import { canShowSpiritualForUser } from '../../utils/spiritualApi';
import UserDayHero from './UserDayHero';
import WelcomeVideoHeaderButton from './WelcomeVideoHeaderButton';
import PendingPaymentBanner from './PendingPaymentBanner';
import D28dCycleBanner from '../d28d/D28dCycleBanner';
import D28dUserHub from '../d28d/D28dUserHub';
import D28dRecordingsPreview from '../d28d/D28dRecordingsPreview';
import TrainingProgressPanel from '../training/TrainingProgressPanel';
import CoachRoutineHomeCard from '../training/CoachRoutineHomeCard';
import SpiritualHomeButton from '../spiritual/user/SpiritualHomeButton';
import CollapsibleSection from '../ui/CollapsibleSection';
import VisualProgressBar from '../ui/VisualProgressBar';
import '../../styles/ecosystem-fit-ux.css';

export default function UserHomeCenter({
  onNavigate,
  onOpenSpiritual,
  gymBrand = null,
}) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { config: frontendConfig } = useFrontendConfig();

  const services = useMemo(
    () => getServicesFor(user, frontendConfig, lang),
    [user, frontendConfig, lang],
  );

  const hasD28d = services.some((s) => s.id === 'd28d' || s.id === 'live-classes');
  const coachClient = isCoachClientUser(user);
  const accessBlocked = isConsumerAccessBlocked(user);
  const foodOnly = isFoodOnlyCommercialUser(user);
  const hasTraining = !accessBlocked && services.some((s) => s.id === 'training');
  const hideFoodUi = hideFoodForGymVirtualConsumer(user, gymBrand);
  const hasFood = !hideFoodUi && services.some((s) => s.id === 'food-plan');
  const hasFutbol = services.some((s) => s.id === 'futbol');
  const coachWl = isCoachWhiteLabelActive(user, mergeCoachBrand(user, null));
  const isSedeFinal = isSedeFinalUser(user, gymBrand);
  const hideD28dBranding = coachWl || (coachClient && !hasD28d) || isSedeFinal;
  const programId = getD28dProgramId(user);
  const virtualGymMember = isVirtualGymMember(user);
  const showSpiritual = canShowSpiritualForUser(user);
  const showContentGallery = hasTraining && !hasD28d;
  // CTA Food: solo fuera de gym virtual/sede (coach / plataforma).
  const coachFoodUpsell = false;
  const sedeFoodUpsell = false;
  const showWelcomeVideoHeader = (hasD28d && !hideD28dBranding && !foodOnly) || isSedeFinal;
  const greetName = user?.nombre ? user.nombre.split(' ')[0] : null;
  const [foodError, setFoodError] = useState('');
  const [futbolError, setFutbolError] = useState('');

  const openProgress = (tab = 'general') => onNavigate?.('mi-progreso', tab);
  const openClasses = () => openProgress('clases');
  const openFoodPurchase = () => redirectToContratarFood();
  const openFood = async () => {
    setFoodError('');
    const result = await openFoodModule('/dashboard', { user, myServices: services });
    if (!result?.ok && !result?.redirecting) {
      setFoodError(result?.error || 'No se pudo abrir FOOD PLAN. Reintenta o contacta soporte.');
    }
  };
  const openTraining = () => {
    if (isTrainingExternal()) {
      openTrainingModule('/dashboard', '/athlete', user);
      return;
    }
    openProgress('entrenamiento');
  };
  const openGallery = () => {
    if (isTrainingExternal()) {
      openTrainingModule('/dashboard', '/athlete/gallery', user);
      return;
    }
    onNavigate?.('training-gallery');
  };

  const secondaryBlocks = [];

  if (showContentGallery && !accessBlocked) {
    secondaryBlocks.unshift({
      key: 'gallery',
      title: t('home.library_title', 'Biblioteca de entrenamiento'),
      hint: t('home.library_hint', 'Videos por ejercicio, recientes y favoritos.'),
      cta: t('home.open_gallery', 'Abrir biblioteca'),
      onClick: openGallery,
      primary: false,
    });
  }
  if (showSpiritual) {
    secondaryBlocks.push({
      key: 'spiritual',
      title: t('nav.spiritual', 'Viento Recio'),
      hint: t('home.spiritual_hint', 'Devocional y contenido espiritual.'),
      render: () => <SpiritualHomeButton onOpen={onOpenSpiritual} />,
    });
  }
  secondaryBlocks.push({
    key: 'support',
    title: t('nav.support', 'Soporte'),
    hint: t('home.support_hint', 'FAQ, asistente y WhatsApp.'),
    cta: t('support.open', 'Abrir soporte'),
    onClick: () => onNavigate?.('support'),
    primary: true,
  });

  return (
    <div className="dashboard-main-view ef-home space-y-5">
      <header>
        <p className="text-xs uppercase tracking-wider d28d-text-muted">
          {greetName
            ? t('services.greeting', 'Hola, {name}', { name: greetName })
            : t('services.greeting_short', 'Hola')}
        </p>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
          <h1 className="d28d-page-title text-2xl mb-0">
            {foodOnly
              ? t('nav.food_plan', 'FOOD PLAN')
              : t('nav.home', 'Inicio')}
          </h1>
          {showWelcomeVideoHeader ? <WelcomeVideoHeaderButton /> : null}
        </div>
      </header>

      {accessBlocked ? (
        <>
          <PendingPaymentBanner onOpenAccount={() => onNavigate?.('myaccount')} />
          <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="font-bold text-[var(--d28d-text)] text-sm">{t('nav.support', 'Soporte')}</h3>
              <p className="text-xs d28d-text-muted mt-1">{t('home.support_hint', 'FAQ, asistente y WhatsApp.')}</p>
            </div>
            <button type="button" className="btn btn-secondary text-sm shrink-0" onClick={() => onNavigate?.('support')}>
              {t('support.open', 'Abrir soporte')}
            </button>
          </div>
        </>
      ) : (
        <>
      {hasFutbol ? (
        <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-bold text-[var(--d28d-text)] text-sm">futbol.app</h3>
            <p className="text-xs d28d-text-muted mt-1">
              {resolveFutbolBrandName(user, 'Tu escuela')}. Entra con tu rol de la escuela.
            </p>
            {futbolError ? <p className="text-sm text-red-700 mt-2" role="alert">{futbolError}</p> : null}
          </div>
          <button
            type="button"
            className="btn btn-primary text-sm shrink-0"
            onClick={async () => {
              setFutbolError('');
              const out = await openFutbolModule('/dashboard', futbolDestForUser(user));
              if (!out?.ok) setFutbolError(out?.error || 'No se pudo abrir futbol.app');
            }}
          >
            Abrir futbol.app
          </button>
        </div>
      ) : null}

      {foodError ? (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
          {foodError}
        </p>
      ) : null}

      <UserDayHero
        hasD28d={((hasD28d && !hideD28dBranding) || isSedeFinal) && !foodOnly}
        hasTraining={hasTraining && !hasD28d && !isSedeFinal}
        hasFood={hasFood}
        coachClient={coachClient}
        coachFoodUpsell={coachFoodUpsell}
        sedeFoodUpsell={sedeFoodUpsell}
        hideD28dBranding={hideD28dBranding}
        sedeFinalMode={isSedeFinal}
        programId={programId}
        onOpenClasses={openClasses}
        onOpenTraining={openTraining}
        onOpenGallery={openGallery}
        onOpenFood={openFood}
        onAcquireFood={openFoodPurchase}
        onOpenProgress={openProgress}
      />

      {hasTraining && coachClient && !hasD28d && !isSedeFinal ? (
        <CoachRoutineHomeCard onOpenTraining={openTraining} onOpenGallery={openGallery} />
      ) : null}

      {hasD28d && !foodOnly && !hideD28dBranding && !isSedeFinal ? (
        <>
          <D28dCycleBanner />
          <D28dUserHub
            gymBrand={gymBrand}
            onNavigate={(view, tab) => {
            if (tab) onNavigate?.(view, tab);
            else onNavigate?.(view);
          }}
          />
          <D28dRecordingsPreview onOpenAll={() => onNavigate?.('d28d-gallery')} />
        </>
      ) : null}

      {isSedeFinal ? (
        <>
          <D28dUserHub
          gymBrand={gymBrand}
          hidePremiacion
          sedeFinalMode
          onNavigate={(view, tab) => {
            if (tab) onNavigate?.(view, tab);
            else onNavigate?.(view);
          }}
        />
        </>
      ) : null}

      {/* Resultados detallados viven en Mi Progreso (evita duplicar dashboard con Inicio). */}
      <section className="space-y-3">
            {(hasD28d && !foodOnly && !hideD28dBranding) || isSedeFinal ? (
              <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide">
                    {t('home.progress_summary', 'Tu progreso')}
                  </h2>
                  <p className="text-xs d28d-text-muted mt-1">
                    {isSedeFinal
                      ? t(
                        'home.progress_cta_hint_sede',
                        'Adherencia, asistencia y resultados de tu vigencia están en Mi Progreso.',
                      )
                      : t(
                        'home.progress_cta_hint',
                        'Adherencia, asistencia y resultados del ciclo están en Mi Progreso.',
                      )}
                  </p>
                </div>
                <button type="button" className="btn btn-primary text-sm shrink-0" onClick={() => openProgress('general')}>
                  {t('home.open_progress', 'Ver mi progreso')}
                </button>
              </div>
            ) : null}
            {hasTraining && !hasD28d && !isSedeFinal ? <TrainingProgressPanel compact showContinue onContinue={openTraining} /> : null}
            {!hasD28d && !hasTraining && !isSedeFinal && (
              <div className="card p-4">
                <VisualProgressBar
                  label={t('home.progress_empty', 'Actividad semanal')}
                  value={0}
                  max={100}
                  hint={t('home.no_progress_yet', 'Tu progreso aparecerá aquí cuando actives un servicio.')}
                />
              </div>
            )}
          </section>

          {(hasFood || hasTraining || isSedeFinal || (hasD28d && !hideD28dBranding)) && (
            <section className="ef-stat-grid">
              {hasFood ? (
                <button type="button" className="ef-stat-card text-left hover:border-[var(--d28d-yellow)] transition" onClick={openFood}>
                  <span className="ef-stat-card__label">{t('home.my_nutrition', 'Alimentación')}</span>
                  <span className="ef-stat-card__value">{t('mi_progreso.open_food', 'Ir a Food Plan →')}</span>
                </button>
              ) : null}
              {hasTraining && showContentGallery ? (
                <button type="button" className="ef-stat-card text-left hover:border-[var(--d28d-yellow)] transition" onClick={openGallery}>
                  <span className="ef-stat-card__label">{t('home.library_title', 'Biblioteca de entrenamiento')}</span>
                  <span className="ef-stat-card__value">{t('home.open_gallery', 'Abrir biblioteca →')}</span>
                </button>
              ) : null}
              {hasTraining ? (
                <button type="button" className="ef-stat-card text-left hover:border-[var(--d28d-yellow)] transition" onClick={openTraining}>
                  <span className="ef-stat-card__label">{t('home.my_training', 'Entrenamiento')}</span>
                  <span className="ef-stat-card__value">{t('home.hero.training_cta_short', 'Rutina →')}</span>
                </button>
              ) : null}
              {hasD28d && !foodOnly && !hideD28dBranding && !isSedeFinal ? (
                <button type="button" className="ef-stat-card text-left hover:border-[var(--d28d-yellow)] transition" onClick={openClasses}>
                  <span className="ef-stat-card__label">{t('home.live_classes', 'Clases')}</span>
                  <span className="ef-stat-card__value">{t('home.open_calendar', 'Calendario →')}</span>
                </button>
              ) : null}
              {isSedeFinal ? (
                <>
                  <button type="button" className="ef-stat-card text-left hover:border-[var(--d28d-yellow)] transition" onClick={openClasses}>
                    <span className="ef-stat-card__label">{t('home.live_classes', 'Clases')}</span>
                    <span className="ef-stat-card__value">{t('home.open_calendar', 'Calendario →')}</span>
                  </button>
                  <button type="button" className="ef-stat-card text-left hover:border-[var(--d28d-yellow)] transition" onClick={() => onNavigate?.('d28d-gallery')}>
                    <span className="ef-stat-card__label">{t('nav.gallery_classes', 'GRABACIONES DE CLASE')}</span>
                    <span className="ef-stat-card__value">{t('home.open_recordings', 'Ver grabaciones →')}</span>
                  </button>
                </>
              ) : null}
            </section>
          )}

      {secondaryBlocks.length > 0 ? (
        <CollapsibleSection
          title={t('home.more_access', 'Más accesos')}
          subtitle={t('home.more_access_hint', 'Biblioteca, soporte y contenido adicional')}
          defaultOpen={showContentGallery || coachClient}
        >
          {secondaryBlocks.map((block) => (
            <div key={block.key} className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="font-bold text-[var(--d28d-text)] text-sm">{block.title}</h3>
                {block.hint ? <p className="text-xs d28d-text-muted mt-1">{block.hint}</p> : null}
                {block.render ? <div className="mt-2">{block.render()}</div> : null}
              </div>
              {block.cta && block.onClick ? (
                <button
                  type="button"
                  className={block.primary ? 'btn btn-primary text-sm shrink-0' : 'btn btn-secondary text-sm shrink-0'}
                  onClick={block.onClick}
                >
                  {block.cta}
                </button>
              ) : null}
            </div>
          ))}
        </CollapsibleSection>
      ) : null}
        </>
      )}
    </div>
  );
}
